import { Box, Typography } from '@mui/material';
import { t } from 'i18next';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { formatDateTime } from '../../utils/dateUtils';
import CustomNoRowsOverlay from '../Table/CustomNoRowsOverlay';
import { IActivityDetails } from '../../redux/client/clientSlice';

interface IRenderCellParams {
  row: IActivityDetails;
  value: string | boolean;
}

const ProgressDetailsTable = () => {
  const activity = useAppSelector((state) => state.client.activity);
  const [filteredData, setFilteredData] = useState<IActivityDetails[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const getRowClassName = () => 'no-border';
  const data = activity.activityDetails;
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  useEffect(() => {
    if (data) {
      setFilteredData(data);
      setRowCount(data.length);
    }
  }, [activity, paginationModel]);

  const columns = [
    { field: 'id', headerName: 'ID', hide: true },
    {
      field: 'Date',
      headerName: 'Date',
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 156,
      renderHeader: () => (
        <Box display="flex">
          <Typography variant="meta" color="primary">
            {t('common.date')}
          </Typography>
        </Box>
      ),
      renderCell: (params: IRenderCellParams) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="meta" color="primary">
            {formatDateTime(params.row.completedAt).formattedDay}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Time',
      headerName: 'Time',
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 156,
      renderHeader: () => (
        <Box display="flex">
          <Typography variant="meta" color="primary">
            {t('common.completedTime')}
          </Typography>
        </Box>
      ),
      renderCell: (params: IRenderCellParams) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="bodyRegular" color="primary">
            {formatDateTime(params.row.completedAt).formattedTime}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box height="100%" display="flex" flexDirection="column" gap={2}>
      <Typography variant="h6">{t('common.log')}</Typography>
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        bgcolor="#fff"
        borderRadius="10px"
        minHeight="60vh"
      >
        <Box
          flexGrow="1"
          height="100%"
          sx={{
            '&::-webkit-scrollbar': {
              width: '0px',
              background: 'transparent',
            },
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
          }}
        >
          <DataGrid
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rows={filteredData}
            rowCount={rowCount}
            paginationMode="client"
            columns={columns as GridColDef[]}
            getRowClassName={getRowClassName}
            columnVisibilityModel={{
              id: false,
            }}
            pageSizeOptions={[10, 15, 20]}
            disableRowSelectionOnClick
            columnHeaderHeight={50}
            getRowHeight={() => 'auto'}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            sx={{
              border: '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)',
              borderColor: '#FFDDC8',
              borderRadius: '10px',
              height: '100%',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)',
                paddingLeft: '24px',
                borderColor: '#FFDDC8',
                cursor: 'pointer',
                paddingY: '12px !important',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: (theme) => theme.palette.primary.main,
                fontWeight: 400,
              },
              '& .MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                paddingLeft: '14px',
                border: 'none',
                whiteSpace: 'normal',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderColor: '#FFDDC8',
              },
              '& .MuiDataGrid-footerContainer': {
                borderColor: '#6CA3AC',
                borderTop: '1px solid #6CA3AC',
              },
              '& .MuiTablePagination-root button': {
                mt: '4px',
                color: '#6CA3AC',
                '&:disabled': {
                  opacity: 0.5,
                },
              },
              '& .MuiTablePagination-input': {
                color: 'primary.main',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: '18px',
                mt: '4px',
                ml: 0,
                pl: 0,
              },
              '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
                color: '#7CAEBE',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18px',
              },
              '& .MuiTablePagination-displayedRows': {
                color: 'primary.main',
                fontFamily: 'Satoshi',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: '18px',
                mt: '4px',
              },
              '& .MuiDataGrid-virtualScroller': {
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '20px',
                  border: '3px solid #f5f5f5',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#888',
                },
              },
            }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressDetailsTable;
